
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Container, Draggable } from 'vue-smooth-dnd';
import { InventoryLocationSettingList } from '@/collections/inventory_location_settings';
import { InventoryTransferRouteList } from '@/collections/inventory_transfer_routes';
import { InventoryLocationSettingModel } from '@/models/inventory_location_setting';
import { InventoryTransferRouteModel } from '@/models/inventory_transfer_route';
import { StoreLocationList } from '@/collections/store_locations';
import { StoreLocationModel } from '@/models/store_location';
import { displayAlert } from '@/services/alert';
import { cloneDeep } from 'lodash';
import { getParameterLabel } from '@/config/parameterLabels';

@Component({
  components: {
    Container,
    Draggable,
  },
})
export default class InventoryTransferSettings extends Vue {
  // Collections
  public locationSettingsCollection = new InventoryLocationSettingList();
  public transferRoutesCollection = new InventoryTransferRouteList();
  public locationList = new StoreLocationList();

  // Data properties
  public allLocations: StoreLocationModel[] = [];
  public transferRoutes: InventoryTransferRouteModel[] = [];

  // Loading states
  public loadingLocations = false;
  public savingEditedLocation = false;
  public loadingRoutes = false;
  public savingRoute = false;
  public isPrioritySaving = false;

  // Dialog states
  public showNewRouteDialog = false;
  public showDeleteConfirmDialog = false;
  public showLocationEditDialog = false;

  // Edited items
  public editedRoute: Partial<InventoryTransferRouteModel> = {};
  public routeToDelete: InventoryTransferRouteModel | null = null;
  public editedLocation: any = null;

  // Other component state
  public isFormValid = false;
  public isLocationFormValid = false;
  public newlyAddedRouteId: number | null = null;
  public strategyOptions = [
    { text: 'Forecast-Based', value: 'FORECAST', short: 'Fc' },
    { text: 'Min/Max Quantity', value: 'MIN_MAX', short: 'M' },
    { text: 'Fixed Quantity', value: 'FIXED_QUANTITY', short: 'FQ' },
  ];

  public leadTimeRules = [
    (v: any) => (v !== null && v !== '') || 'Lead time is required.',
    (v: any) => v >= 0 || 'Lead time cannot be negative.',
  ];

  // Computed properties for standardized labels
  get safetyStockDaysLabel(): string {
    return getParameterLabel('SAFETY_STOCK_DAYS', 'label');
  }

  get targetStockDaysLabel(): string {
    return getParameterLabel('TARGET_STOCK_DAYS', 'label');
  }

  get minStockDaysLabel(): string {
    return getParameterLabel('MIN_STOCK_DAYS', 'label');
  }

  get maxStockDaysLabel(): string {
    return getParameterLabel('MAX_STOCK_DAYS', 'label');
  }

  get minStockQtyLabel(): string {
    return getParameterLabel('MIN_STOCK_QUANTITY', 'label');
  }

  get maxStockQtyLabel(): string {
    return getParameterLabel('MAX_STOCK_QUANTITY', 'label');
  }

  get leadTimeLabel(): string {
    return getParameterLabel('LEAD_TIME', 'label');
  }

  public rules = {
    required: (v: any) => (v !== null && v !== undefined && v !== '') || 'Field is required.',
    notNegative: (v: number) => v >= 0 || 'Cannot be negative.',
    isInteger: (v: any) => {
      if (v === null || v === undefined || v === '') return true;
      return Number.isInteger(Number(v)) || 'Must be a whole number.';
    },
    safetyLessThanTarget: (v: number) => {
      if (this.editedLocation && (this.editedLocation.targetStockDays !== null && this.editedLocation.targetStockDays !== undefined && this.editedLocation.targetStockDays !== '')) {
        return v < Number(this.editedLocation.targetStockDays) || 'Safety stock must be less than target stock.';
      }
      return true;
    },
    targetGreaterThanSafety: (v: number) => {
      if (this.editedLocation && (this.editedLocation.safetyStockDays !== null && this.editedLocation.safetyStockDays !== undefined && this.editedLocation.safetyStockDays !== '')) {
        return v > Number(this.editedLocation.safetyStockDays) || 'Target stock must be greater than safety stock.';
      }
      return true;
    },
    minLessThanMax: (v: number) => {
      if (this.editedLocation && (this.editedLocation.maxDays !== null && this.editedLocation.maxDays !== undefined && this.editedLocation.maxDays !== '')) {
        return v < Number(this.editedLocation.maxDays) || 'Min days must be less than max days.';
      }
      return true;
    },
    maxGreaterThanMin: (v: number) => {
      if (this.editedLocation && (this.editedLocation.minDays !== null && this.editedLocation.minDays !== undefined && this.editedLocation.minDays !== '')) {
        return v > Number(this.editedLocation.minDays) || 'Max days must be greater than min days.';
      }
      return true;
    },
    minQuantityLessThanMax: (v: number) => {
      if (this.editedLocation && (this.editedLocation.maxQuantity !== null && this.editedLocation.maxQuantity !== undefined && this.editedLocation.maxQuantity !== '')) {
        return v < Number(this.editedLocation.maxQuantity) || 'Min quantity must be less than max quantity.';
      }
      return true;
    },
    maxQuantityGreaterThanMin: (v: number) => {
      if (this.editedLocation && (this.editedLocation.minQuantity !== null && this.editedLocation.minQuantity !== undefined && this.editedLocation.minQuantity !== '')) {
        return v > Number(this.editedLocation.minQuantity) || 'Max quantity must be greater than min quantity.';
      }
      return true;
    }
  };

  get isSmallScreen(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this as any).$vuetify.breakpoint.width < 1200;
  }

  get hasOnlyOneLocation(): boolean {
    return this.allLocations.length <= 1;
  }

  get mergedLocationData() {
    return this.allLocations.map(loc => {
      const setting = this.locationSettingsCollection.items.find(s => s.locationId === String(loc.id));
      return {
        ...loc,
        ...(setting ? cloneDeep(setting) : new InventoryLocationSettingModel({ location_id: loc.id })),
      };
    });
  }

  get groupedRoutes() {
    if (!this.transferRoutes || this.transferRoutes.length === 0) {
      return [];
    }

    const groups: {
      [key: number]: {
        sourceId: number;
        sourceName: string;
        routes: InventoryTransferRouteModel[];
      }
    } = {};

    this.transferRoutes.forEach(route => {
      const sourceId = route.sourceLocationId;
      if (sourceId) {
        if (!groups[sourceId]) {
          groups[sourceId] = {
            sourceId: sourceId,
            sourceName: `From: ${this.getLocationName(sourceId) || 'Unknown'}`,
            routes: []
          };
        }
        groups[sourceId].routes.push(route);
      }
    });

    return Object.values(groups).map(group => {
      group.routes.sort((a, b) => a.priority - b.priority);
      return group;
    });
  }

  // Computed error messages for immediate validation display
  get minDaysError(): string[] {
    if (!this.editedLocation || this.editedLocation.strategy !== 'MIN_MAX') {
      return [];
    }
    const minDays = this.editedLocation.minDays;
    const maxDays = this.editedLocation.maxDays;

    if (minDays !== null && minDays !== undefined && minDays !== '' &&
        maxDays !== null && maxDays !== undefined && maxDays !== '') {
      if (Number(minDays) >= Number(maxDays)) {
        return ['Min days must be less than max days'];
      }
    }
    return [];
  }

  get maxDaysError(): string[] {
    if (!this.editedLocation || this.editedLocation.strategy !== 'MIN_MAX') {
      return [];
    }
    const minDays = this.editedLocation.minDays;
    const maxDays = this.editedLocation.maxDays;

    if (minDays !== null && minDays !== undefined && minDays !== '' &&
        maxDays !== null && maxDays !== undefined && maxDays !== '') {
      if (Number(maxDays) <= Number(minDays)) {
        return ['Max days must be greater than min days'];
      }
    }
    return [];
  }

  get minQuantityError(): string[] {
    if (!this.editedLocation || this.editedLocation.strategy !== 'FIXED_QUANTITY') {
      return [];
    }
    const minQty = this.editedLocation.minQuantity;
    const maxQty = this.editedLocation.maxQuantity;

    if (minQty !== null && minQty !== undefined && minQty !== '' &&
        maxQty !== null && maxQty !== undefined && maxQty !== '') {
      if (Number(minQty) >= Number(maxQty)) {
        return ['Min quantity must be less than max quantity'];
      }
    }
    return [];
  }

  get maxQuantityError(): string[] {
    if (!this.editedLocation || this.editedLocation.strategy !== 'FIXED_QUANTITY') {
      return [];
    }
    const minQty = this.editedLocation.minQuantity;
    const maxQty = this.editedLocation.maxQuantity;

    if (minQty !== null && minQty !== undefined && minQty !== '' &&
        maxQty !== null && maxQty !== undefined && maxQty !== '') {
      if (Number(maxQty) <= Number(minQty)) {
        return ['Max quantity must be greater than min quantity'];
      }
    }
    return [];
  }

  // Watchers
  @Watch('editedLocation.safetyStockDays')
  onSafetyStockDaysChange() {
    if (this.editedLocation && this.editedLocation.strategy === 'FORECAST') {
      this.$nextTick(() => {
        const form = this.$refs.locationForm as any;
        if (form) {
          form.validate();
        }
      });
    }
  }

  @Watch('editedLocation.targetStockDays')
  onTargetStockDaysChange() {
    if (this.editedLocation && this.editedLocation.strategy === 'FORECAST') {
      this.$nextTick(() => {
        const form = this.$refs.locationForm as any;
        if (form) {
          form.validate();
        }
      });
    }
  }

  @Watch('editedLocation.minDays')
  onMinDaysChange() {
    if (this.editedLocation && this.editedLocation.strategy === 'MIN_MAX') {
      this.$nextTick(() => {
        const form = this.$refs.locationForm as any;
        if (form) {
          form.validate();
        }
      });
    }
  }

  @Watch('editedLocation.maxDays')
  onMaxDaysChange() {
    if (this.editedLocation && this.editedLocation.strategy === 'MIN_MAX') {
      this.$nextTick(() => {
        const form = this.$refs.locationForm as any;
        if (form) {
          form.validate();
        }
      });
    }
  }

  @Watch('editedLocation.minQuantity')
  onMinQuantityChange() {
    if (this.editedLocation && this.editedLocation.strategy === 'FIXED_QUANTITY') {
      this.$nextTick(() => {
        const form = this.$refs.locationForm as any;
        if (form) {
          form.validate();
        }
      });
    }
  }

  @Watch('editedLocation.maxQuantity')
  onMaxQuantityChange() {
    if (this.editedLocation && this.editedLocation.strategy === 'FIXED_QUANTITY') {
      this.$nextTick(() => {
        const form = this.$refs.locationForm as any;
        if (form) {
          form.validate();
        }
      });
    }
  }

  // Lifecycle
  async created() {
    this.loadingLocations = true;
    this.loadingRoutes = true;
    try {
      await Promise.all([
        this.locationList.fetch({limit: 250}),
        this.locationSettingsCollection.fetch(),
        this.transferRoutesCollection.fetch(),
      ]);
      this.allLocations = this.locationList.items;
      this.transferRoutes = this.transferRoutesCollection.items;
    } catch (error) {
      displayAlert('Failed to load initial settings data.');
      console.error(error);
    } finally {
      this.loadingLocations = false;
      this.loadingRoutes = false;
    }
  }

  // Methods
  getLocationName(locationId: number | null): string | undefined {
    if (!locationId) return '';
    return this.allLocations.find(loc => loc.id === locationId)?.name;
  }

  openLocationEditDialog(item: any) {
    this.editedLocation = cloneDeep(item);
    this.showLocationEditDialog = true;
  }

  closeLocationEditDialog() {
    this.showLocationEditDialog = false;
    this.editedLocation = null;
  }

  async saveEditedLocation() {
    if (this.editedLocation) {
      this.savingEditedLocation = true;
      const modelData = cloneDeep(this.editedLocation);
      const isExistingSetting = this.locationSettingsCollection.items.some(s => s.id === modelData.id);
      if (!isExistingSetting) {
        delete modelData.id;
      }
      const locationSetting = new InventoryLocationSettingModel(modelData);
      try {
        await locationSetting.save();
        await this.locationSettingsCollection.fetch(); // Refetch data
        displayAlert('Location setting saved successfully.');
        this.closeLocationEditDialog();
      } catch (error) {
        displayAlert('Failed to save location setting.');
        console.error(error);
      } finally {
        this.savingEditedLocation = false;
      }
    }
  }

  getStrategyFullName(strategy: string) {
    const option = this.strategyOptions.find(o => o.value === strategy);
    return option ? option.text : '';
  }

  getSettingsString(item: any): string {
    if (item.strategy === 'FORECAST') {
      const safety = this.formatSettingNumber(item.safetyStockDays, 'days');
      const target = this.formatSettingNumber(item.targetStockDays, 'days');
      return `${getParameterLabel('SAFETY_STOCK_DAYS', 'short')}: ${safety}, ${getParameterLabel('TARGET_STOCK_DAYS', 'short')}: ${target}`;
    } else if (item.strategy === 'MIN_MAX') {
      const min = this.formatSettingNumber(item.minDays, 'days');
      const max = this.formatSettingNumber(item.maxDays, 'days');
      return `${getParameterLabel('MIN_STOCK_DAYS', 'short')}: ${min}, ${getParameterLabel('MAX_STOCK_DAYS', 'short')}: ${max}`;
    } else if (item.strategy === 'FIXED_QUANTITY') {
      const min = this.formatSettingNumber(item.minQuantity, 'units');
      const max = this.formatSettingNumber(item.maxQuantity, 'units');
      return `${getParameterLabel('MIN_STOCK_QUANTITY', 'short')}: ${min}, ${getParameterLabel('MAX_STOCK_QUANTITY', 'short')}: ${max}`;
    }
    return 'Not configured';
  }

  formatSettingNumber(value: number | null | undefined, unit = ''): string {
    if (value === null || typeof value === 'undefined' || isNaN(value)) {
      return 'Not Set';
    }
    return `${String(value)}${unit ? ` ${unit}` : ''}`;
  }

  async onDrop(group: any, dropResult: any) {
    const { removedIndex, addedIndex } = dropResult;
    if (removedIndex === null && addedIndex === null) return;

    this.isPrioritySaving = true;

    // 1. Store original state for rollback
    const originalRoutes = cloneDeep(this.transferRoutes);

    // 2. Create a temporary, mutated version of the group's routes to determine the new priorities
    const tempRoutes = [...group.routes];
    const movedItem = tempRoutes.splice(removedIndex, 1)[0];
    tempRoutes.splice(addedIndex, 0, movedItem);

    // 3. Calculate the new priorities based on the temporary, reordered list
    const updatedPriorities = tempRoutes.map((route, index) => ({
      id: route.id as number,
      priority: index + 1,
    }));

    // 4. Perform the optimistic UI update by applying the new priorities directly to the main data source.
    // This avoids flickering by making the optimistic and final states identical.
    this.transferRoutes.forEach(route => {
      const updatedRoute = updatedPriorities.find(p => p.id === route.id);
      if (updatedRoute) {
        route.priority = updatedRoute.priority;
      }
    });

    try {
      // 5. Make the API call
      await this.transferRoutesCollection.batchUpdatePriorities(group.sourceId, updatedPriorities);
      displayAlert('Route priorities have been updated.');
    } catch (error) {
      // 6. On failure, revert the changes by restoring the original data
      console.error('API call failed:', error);
      displayAlert('Failed to update route priorities. Reverting changes.');
      this.transferRoutes = originalRoutes;
    } finally {
      this.isPrioritySaving = false;
    }
  }

  openNewRouteDialog() {
    const newPriority = (this.transferRoutes.length > 0) ? Math.max(...this.transferRoutes.map(r => r.priority)) + 1 : 1;
    this.editedRoute = {
      priority: newPriority,
      leadTimeDays: 0,
    };
    this.showNewRouteDialog = true;
  }

  editRoute(route: InventoryTransferRouteModel) {
    this.editedRoute = cloneDeep(route);
    this.showNewRouteDialog = true;
  }

  closeDialog() {
    this.showNewRouteDialog = false;
    this.editedRoute = {};
  }

  async saveRoute() {
    this.savingRoute = true;
    const isNewRoute = !this.editedRoute.id;
    const editedRouteId = this.editedRoute.id;
    const routeModel = new InventoryTransferRouteModel(this.editedRoute);

    try {
      const originalRouteIds = new Set(this.transferRoutes.map(r => r.id));
      await routeModel.save();
      displayAlert('Transfer route saved successfully.');
      this.closeDialog();

      await this.fetchTransferRoutes();

      if (isNewRoute) {
        const newRoute = this.transferRoutes.find(r => !originalRouteIds.has(r.id));
        if (newRoute && newRoute.id) {
          this.newlyAddedRouteId = newRoute.id;
          this.scrollToAndHighlight(this.newlyAddedRouteId);
        }
      } else if (editedRouteId) {
        this.newlyAddedRouteId = editedRouteId;
        this.scrollToAndHighlight(editedRouteId);
      }
    } catch (error) {
      displayAlert('Failed to save transfer route.');
      console.error(error);
    } finally {
      this.savingRoute = false;
    }
  }

  deleteRoute(route: InventoryTransferRouteModel) {
    this.routeToDelete = route;
    this.showDeleteConfirmDialog = true;
  }

  closeDeleteDialog() {
    this.showDeleteConfirmDialog = false;
    this.routeToDelete = null;
  }

  async confirmDelete() {
    if (this.routeToDelete) {
      const routeToDelete = this.routeToDelete;
      try {
        await this.transferRoutesCollection.delete(routeToDelete);
        const index = this.transferRoutes.findIndex(r => r.id === routeToDelete.id);
        if (index > -1) {
          this.transferRoutes.splice(index, 1);
        }
        displayAlert('Transfer route deleted successfully.');
      } catch (error) {
        displayAlert('Failed to delete transfer route.');
        console.error(error);
        // Optional: Rollback on error by refetching
        this.fetchTransferRoutes();
      } finally {
        this.closeDeleteDialog();
      }
    }
  }

  scrollToAndHighlight(routeId: number) {
    this.$nextTick(() => {
      const refName = `route-item-${routeId}`;
      const elementRef = this.$refs[refName];

      if (elementRef && Array.isArray(elementRef) && elementRef.length > 0) {
        const element = elementRef[0] as HTMLElement;
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => {
            this.newlyAddedRouteId = null;
          }, 2500);
        }
      }
    });
  }

  async fetchTransferRoutes() {
    this.loadingRoutes = true;
    try {
      await this.transferRoutesCollection.fetch();
      this.transferRoutes = this.transferRoutesCollection.items;
    } catch (error) {
      displayAlert('Failed to fetch transfer routes.');
      console.error(error);
    } finally {
      this.loadingRoutes = false;
    }
  }

  get isSaveDisabled() {
    return !this.editedRoute.sourceLocationId ||
      !this.editedRoute.destinationLocationId ||
      this.editedRoute.sourceLocationId === this.editedRoute.destinationLocationId ||
      this.isRouteDuplicate;
  }

  get isRouteDuplicate(): boolean {
    if (!this.editedRoute.sourceLocationId || !this.editedRoute.destinationLocationId) {
      return false;
    }

    return this.transferRoutes.some(route =>
      route.sourceLocationId === this.editedRoute.sourceLocationId &&
      route.destinationLocationId === this.editedRoute.destinationLocationId &&
      route.id !== this.editedRoute.id
    );
  }

  get duplicateRouteError(): string {
    return this.isRouteDuplicate ? 'This transfer route already exists.' : '';
  }

  get availableSourceLocations() {
    const destId = this.editedRoute.destinationLocationId;

    if (!destId) {
      return this.allLocations;
    }

    const allExceptDest = this.allLocations.filter(loc => loc.id !== destId);

    const usedSourceIds = new Set(
      this.transferRoutes
        .filter(route => route.destinationLocationId === destId && route.id !== this.editedRoute.id)
        .map(route => route.sourceLocationId)
    );

    const available = allExceptDest.filter(l => !usedSourceIds.has(l.id));
    const matched = allExceptDest.filter(l => usedSourceIds.has(l.id));

    const items: any[] = [];
    if (available.length > 0) {
      items.push({ isHeader: true, text: 'Available Sources', id: 'header_availableSource', name: 'header_availableSource' });
      items.push(...available);
    }

    if (matched.length > 0) {
      if (items.length > 0) {
        items.push({ isDivider: true, id: 'dividerSource1', name: 'dividerSource1' });
      }
      items.push({ isHeader: true, text: 'Matched Sources', id: 'headerMatchedSource', name: 'headerMatchedSource' });
      items.push(...matched.map(m => ({ ...m, disabled: true })));
    }

    return items;
  }

  get availableDestinationLocations() {
    const sourceId = this.editedRoute.sourceLocationId;
    if (!this.allLocations || !sourceId) {
      return this.allLocations.map(loc => ({ ...loc, disabled: false }));
    }

    const allExceptSource = this.allLocations.filter(loc => loc.id !== sourceId);

    const usedDestinationIds = new Set(
      this.transferRoutes
        .filter(route => route.sourceLocationId === sourceId && route.id !== this.editedRoute.id)
        .map(route => route.destinationLocationId)
    );

    const available = allExceptSource.filter(l => !usedDestinationIds.has(l.id));
    const matched = allExceptSource.filter(l => usedDestinationIds.has(l.id));

    const items: any[] = [];
    if (available.length > 0) {
      items.push({ isHeader: true, text: 'Available Locations', id: 'headerAvailable', name: 'headerAvailable' });
      items.push(...available);
    }

    if (matched.length > 0) {
      if (items.length > 0) {
        items.push({ isDivider: true, id: 'divider1', name: 'divider1' });
      }
      items.push({ isHeader: true, text: 'Matched Locations', id: 'headerMatched', name: 'headerMatched' });
      items.push(...matched.map(m => ({ ...m, disabled: true })));
    }

    return items;
  }
}
